import videojs from 'video.js';
import { getFullscreenToggleIndex } from '../../util';
import { OverflowMenuButton } from './controlbar-menu/overflow-menu-button.js';

/**
 * @typedef {Object} OverflowMenuItemData
 * @property {string} [id]
 * @property {string} [label]
 * @property {string} [icon]
 * @property {boolean} [visible]
 * @property {function} callback
 * @property {Array<OverflowMenuItemData>} subItems
 */

/** 
 *  @type {WeakMap<ControlbarMenuPlugin, OverflowMenuButton>}
 *  @private
 */
const _menuButton = new WeakMap();

/**
 * The ControlbarMenu module.
 * @member {ControlbarMenuPlugin} controlbarMenu
 * @memberof MediasitePlayer#
 */

/**
 * Provides developers with the ability to add menu items to the control bar.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class ControlbarMenuPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);

        const button = new OverflowMenuButton(player);
        _menuButton.set(this, button);

        player.ready(() => {
            player.getChild('ControlBar').addChild(button, null, getFullscreenToggleIndex(player) + 1);
        });

        player.one('play', () => {
            const controlBar = player.getChild('ControlBar');
            const playerEl = player.contentEl();
            playerEl.style.setProperty('--control-bar-height', `${controlBar.contentEl().clientHeight}px`);
        });
    }

    /**
     * Adds a menu item to the control bar menu.
     * @param {string} label A label to display for the menu item.
     * @param {OverflowMenuItemData} [options]
     * @returns {string} The ID of the menu item.
     * @example
     * const itemId = player.controlbarMenu.addMenuItem('Click here', {
     *     callback: () => console.log('I was clicked!')
     * });
     */
    addMenuItem(label, options) {
        const menuButton = _menuButton.get(this);
        return menuButton.addMenuItem(label, options);
    }

    /**
     * Updates an existing menu item.
     * @param {string} id
     * @param {OverflowMenuItemData} options
     * @example
     * player.controlbarMenu.update('itemId', {
     *     label: 'Changed the label',
     *     callback: () => console.log('Changed the callback')
     * });
     */
    updateMenuItem(id, options) {
        _menuButton.get(this).updateMenuItem(id, options);
    }

    /**
     * Determines if the given menu item is present in the control bar menu.
     * @param {string} itemId The id of a menu item that may or may not be in the menu
     * @returns {boolean}
     * @example
     * const menuHasThisMenuItem = player.controlbarMenu.hasMenuItem('item');
     */
    hasMenuItem(itemId) {
        return _menuButton.get(this).hasItem(itemId);
    }

    /**
     * Determines if the given menu item is enabled and visible in the control bar menu.
     * @param {string} itemId The ID of a menu item that was previously added via [addMenuItem]{@link ControlbarMenuPlugin#addMenuItem}.
     * @returns {boolean}
     * @example
     * const isItemEnabled = player.controlbarMenu.isMenuItemEnabled('itemId');
     */
    isMenuItemEnabled(itemId) {
        return _menuButton.get(this).isItemVisible(itemId);
    }

    /**
     * Shows the given menu item in the control bar menu.
     * @param {string} itemId The ID of a menu item that was previously added via [addMenuItem]{@link ControlbarMenuPlugin#addMenuItem}.
     * @example
     * player.controlbarMenu.enableMenuItem('itemId');
     */
    enableMenuItem(itemId) {
        _menuButton.get(this).showMenuItem(itemId);
    }

    /**
     * Hides the given menu item in the control bar menu.
     * @param {string} itemId The ID of a menu item that was previously added via [addMenuItem]{@link ControlbarMenuPlugin#addMenuItem}.
     * @example
     * player.controlbarMenu.disableMenuItem('itemId');
     */
    disableMenuItem(itemId) {
        _menuButton.get(this).hideMenuItem(itemId);
    }

    /**
     * Removes the given menu item from the control bar menu.
     * @param {string} itemId The ID of a menu item that was previously added via [addMenuItem]{@link ControlbarMenuPlugin#addMenuItem}.
     * @example
     * player.controlbarMenu.removeMenuItem('itemId');
     */
    removeMenuItem(itemId) {
        _menuButton.get(this).removeMenuItem(itemId);
    }

    dispose() {
        const menuButton = _menuButton.get(this);
        menuButton.dispose();

        const controlbar = this.player.getChild('ControlBar');
        controlbar.removeChild(menuButton);

        super.dispose();
    }
}

export default ControlbarMenuPlugin;
